// Armado del query param `filters` para los listados del core-api.
//
// Los filtros viajaban como `filter_groups` bracket-expandido, que es el encoding por
// defecto de vue-resource (`filter_groups[0][filters][0][2][0]=...`). Cada valor arrastra
// ~35 caracteres de overhead, así que un filtro con un array grande — el pegado masivo de
// ids en `['id', 'in', ids]` — supera el límite de URL y el request falla.
//
// El core-api acepta la alternativa desde el middleware global `TransformData`, que hace
// `json_decode` del param `filters` en todo GET y mergea sus claves al request. Si viajan
// las dos formas, la JSON pisa a la bracket, así que el call site tiene que borrar
// `params.filter_groups` después de asignar `params.filters`.

/**
 * Serializa los grupos de filtros de un listado al query param `filters`.
 *
 * El saneo previo NO es opcional: la forma bracket-expandida omitía los valores vacíos de
 * manera implícita, mientras que `JSON.stringify` los mandaría explícitos (un quick filter
 * limpiado queda como `[]` y el backend lo rechaza). Se descartan los filtros vacíos y,
 * después, los grupos que quedaron sin ninguno.
 *
 * Se conservan las dos formas de filtro que conviven en las pantallas: el array posicional
 * `['key', 'operador', valor]` y el objeto `{key, operator, value}` (el filtro "sin agendar"
 * de la lista manual de shippings). Las demás claves del grupo — `or: true` — se preservan:
 * perderlas convertiría un OR en un AND sin que nada falle.
 *
 * `null` se preserva tal cual: el backend lo traduce a una condición "es nulo", igual que
 * hacía la forma bracket (que lo mandaba como `''`). Los valores `false` y `0` en cambio se
 * stringifican — ver `normalizeValue`.
 *
 * @param {Array} filterGroups grupos tal como los arma la pantalla
 * @returns {string} el JSON string listo para asignar a `params.filters`
 */
export function toFiltersParam(filterGroups) {
  const groups = (filterGroups || [])
    .map(group => {
      const filters = (group.filters || []).filter(hasValue).map(normalizeFilter)
      return {...group, filters}
    })
    .filter(group => group.filters.length > 0)

  return JSON.stringify({filter_groups: groups})
}

// Un filtro tiene contenido si es un array no vacío o un objeto (la forma
// `{key, operator, value}`). Cualquier otra cosa — null, undefined, primitivos — se descarta.
function hasValue(filter) {
  if (Array.isArray(filter)) {
    return filter.length > 0
  }
  return typeof filter === 'object' && filter !== null
}

// Devuelve una copia del filtro con el valor normalizado. No muta: los objetos de
// `quickFilters` se reusan entre búsquedas.
function normalizeFilter(filter) {
  if (Array.isArray(filter)) {
    if (filter.length < 3 || !needsStringify(filter[2])) {
      return filter
    }
    const normalized = filter.slice()
    normalized[2] = String(normalized[2])
    return normalized
  }
  if (needsStringify(filter.value)) {
    return {...filter, value: String(filter.value)}
  }
  return filter
}

// `false` y `0` se mandan stringificados, que es lo que hacía la forma bracket: el
// `escape(value)` de vue-resource los convertía en "false" y "0", truthy en PHP, y así
// sobrevivían al `$filter[2] ?: null` de `getFilter()` (EloquentBuilderTrait). Mandados
// como falsy nativos ese `?:` los pisa con null, y Laravel traduce `where col = null` a
// `IS NULL`: el filtro deja de filtrar lo que se pidió y el listado vuelve vacío, sin
// error. Pasa en la búsqueda default de ShippingListManual (`is_outsourced eq false`) y en
// los quick filters de users (`active eq false`), tours (`has_relevant_centroid eq 0`),
// transactions (`is_outsourcing eq false`) y events (`active eq false`).
// `null` no entra acá: la bracket lo mandaba como `''` y ambos terminan en `IS NULL`.
function needsStringify(value) {
  return value === false || value === 0
}
